/**
 * Admin Profile – same structure as Admin Payments: hero, role card, tabs (Profile, Stats, Activity).
 */

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../../hooks/useAdmin';
import { useUser } from '../../hooks/useUser';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import AdminLayout from '../../components/admin/AdminLayout';
import Input from '../../components/common/Input';
import Spinner from '../../components/common/Spinner';
import Button from '../../components/common/Button';
import Textarea from '../../components/common/Textarea';
import Avatar from '../../components/common/Avatar';
import UserStats from '../../components/user/UserStats';
import '../../styles/landing.css';
import '../../styles/animations.css';
import '../../styles/global.css';

function normalizeProfile(p) {
  if (!p) return null;
  return {
    ...p,
    fullName: p.full_name ?? p.fullName ?? p.name,
    profilePhotoUrl: p.profile_photo_url ?? p.profilePhotoUrl ?? p.photoUrl,
    createdAt: p.created_at ?? p.createdAt,
    lastLogin: p.last_login ?? p.lastLogin,
  };
}

const AdminProfile = () => {
  const navigate = useNavigate();
  const { loadAdminProfile, loadAdminActivity, loading } = useAdmin();
  const { updateProfile, loading: updateLoading } = useUser();
  const { showError, showSuccess } = useToast();
  const { formatDate, language } = useI18n();

  const [profile, setProfile] = useState(null);
  const [activity, setActivity] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statsInView, setStatsInView] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', bio: '' });
  const statsRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        const [profileRes, activityRes] = await Promise.all([
          loadAdminProfile().catch(() => null),
          loadAdminActivity().catch(() => null),
        ]);
        const admin = profileRes?.admin ?? profileRes?.data ?? profileRes;
        setProfile(admin ?? null);
        const act = activityRes?.activity ?? activityRes?.data ?? activityRes;
        setActivity(Array.isArray(act) ? act : []);
        if (admin) {
          const p = normalizeProfile(admin);
          setFormData({
            name: p?.fullName ?? p?.name ?? '',
            email: p?.email ?? '',
            bio: p?.bio ?? '',
          });
        }
      } catch (err) {
        showError(
          err?.message ||
            (language === 'fr' ? 'Échec du chargement du profil' : 'Failed to load profile')
        );
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [loadAdminProfile, loadAdminActivity, showError, language]);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setStatsInView(true)),
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      await updateProfile({
        fullName: formData.name.trim(),
        bio: formData.bio?.trim() || undefined,
      });
      showSuccess(language === 'fr' ? 'Profil mis à jour.' : 'Profile updated.');
      setIsEditing(false);
      const profileRes = await loadAdminProfile();
      const admin = profileRes?.admin ?? profileRes?.data ?? profileRes;
      setProfile(admin ?? null);
    } catch (err) {
      showError(
        err?.message ||
          (language === 'fr' ? 'Échec de la mise à jour' : 'Failed to update')
      );
    }
  };

  const p = normalizeProfile(profile);
  const tabs = [
    { id: 'profile', label: language === 'fr' ? 'Profil' : 'Profile', icon: '👤' },
    { id: 'stats', label: language === 'fr' ? 'Statistiques' : 'Stats', icon: '📊' },
    { id: 'activity', label: language === 'fr' ? 'Activité' : 'Activity', icon: '📈' },
  ];

  if (isLoading && !profile) {
    return (
      <AdminLayout hideFooter>
        <div className="transactions-wt transactions-wt-loading">
          <div className="dashboard-wt-hero-inner">
            <Spinner size="lg" />
            <p className="mt-4 text-[#a3a3a3]">
              {language === 'fr' ? 'Chargement du profil...' : 'Loading profile...'}
            </p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="transactions-wt dashboard-wt">
        {/* Hero */}
        <section className="dashboard-wt-hero">
          <div className="dashboard-wt-hero-bg" aria-hidden="true" />
          <div className="dashboard-wt-hero-grid" aria-hidden="true" />
          <div className="dashboard-wt-hero-noise" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-1" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-2" aria-hidden="true" />
          <div className="dashboard-wt-hero-inner">
            <h1 className="dashboard-wt-hero-title">
              👤 {language === 'fr' ? 'Mon profil admin' : 'My Admin Profile'}
            </h1>
            <p className="dashboard-wt-hero-sub">
              {language === 'fr'
                ? 'Gérez votre profil d\'administrateur'
                : 'Manage your administrator profile'}
            </p>
            <button
              type="button"
              className="dashboard-wt-btn-primary dashboard-wt-btn-large mt-6"
              onClick={() => navigate('/admin/dashboard')}
            >
              <span className="dashboard-wt-btn-icon">←</span>
              {language === 'fr' ? 'Retour au tableau de bord' : 'Back to Dashboard'}
            </button>
          </div>
        </section>

        {/* Role card */}
        <section className="dashboard-wt-section">
          <div className="dashboard-wt-section-inner">
            <div className={`dashboard-wt-stats ${statsInView ? 'is-inview' : ''}`} ref={statsRef}>
              <div className="dashboard-wt-stat-card" style={{ gridColumn: '1 / -1' }}>
                <div className="dashboard-wt-stat-icon">
                  <span>🔒</span>
                </div>
                <div className="dashboard-wt-stat-content flex-1">
                  <div className="dashboard-wt-stat-value">
                    {profile?.role === 'super_admin'
                      ? language === 'fr'
                        ? 'Super administrateur'
                        : 'Super Administrator'
                      : language === 'fr'
                        ? 'Administrateur'
                        : 'Administrator'}
                  </div>
                  <div className="dashboard-wt-stat-label">
                    {profile?.permissions?.length
                      ? `${profile.permissions.length} ${language === 'fr' ? 'permissions actives' : 'active permissions'}`
                      : language === 'fr'
                        ? 'Rôle administrateur'
                        : 'Administrator role'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Tabs */}
        <section className="dashboard-wt-section">
          <div className="dashboard-wt-section-inner">
            <div className="dashboard-wt-card p-2 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[var(--wt-black-2,#111)]">
              <div className="flex gap-2 flex-wrap">
                {tabs.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setActiveTab(t.id)}
                    className={`flex items-center gap-2 py-3 px-4 rounded-lg font-medium transition-all flex-1 min-w-[120px] ${
                      activeTab === t.id
                        ? 'bg-[rgba(34,197,94,0.15)] text-[#22c55e] border border-[rgba(34,197,94,0.3)]'
                        : 'text-[#a3a3a3] hover:bg-[rgba(255,255,255,0.05)] border border-transparent'
                    }`}
                  >
                    <span>{t.icon}</span>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Tab content */}
        <section className="dashboard-wt-section dashboard-wt-section-alt">
          <div className="dashboard-wt-section-inner">
            {activeTab === 'profile' && (
              <div className="dashboard-wt-card p-6 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[var(--wt-black-2,#111)]">
                <div className="space-y-6">
                  <div className="flex flex-wrap items-center gap-6">
                    <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-gradient-to-br from-blue-500/80 to-green-600/80 flex items-center justify-center">
                      <Avatar
                        src={p?.profilePhotoUrl}
                        name={p?.fullName ?? 'A'}
                        size="xl"
                        variant="circle"
                        className="!w-16 !h-16"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-xl font-bold text-[#e5e5e5] mb-1">
                        {p?.fullName ?? (language === 'fr' ? 'Administrateur' : 'Administrator')}
                      </h2>
                      {p?.email && (
                        <p className="text-[#a3a3a3] text-sm">{p.email}</p>
                      )}
                      {p?.phone && (
                        <p className="text-[#737373] text-sm">{p.phone}</p>
                      )}
                    </div>
                    {!isEditing && (
                      <button
                        type="button"
                        className="dashboard-wt-btn-secondary py-2 px-4"
                        onClick={() => setIsEditing(true)}
                      >
                        ✏️ {language === 'fr' ? 'Modifier' : 'Edit'}
                      </button>
                    )}
                  </div>

                  {isEditing ? (
                    <div className="space-y-4 pt-4 border-t border-[rgba(255,255,255,0.08)]">
                      <div>
                        <label className="block text-sm font-medium text-[#e5e5e5] mb-2">
                          {language === 'fr' ? 'Nom complet' : 'Full name'}
                        </label>
                        <Input
                          type="text"
                          value={formData.name}
                          onChange={(e) => handleChange('name', e.target.value)}
                          placeholder={language === 'fr' ? 'Votre nom' : 'Your name'}
                          className="!bg-[#0a0a0a] !border-[rgba(255,255,255,0.12)] !text-[#fafafa]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#e5e5e5] mb-2">
                          {language === 'fr' ? 'Email' : 'Email'}
                        </label>
                        <Input
                          type="email"
                          value={formData.email}
                          disabled
                          className="!bg-[#0a0a0a] !border-[rgba(255,255,255,0.08)] !text-[#737373]"
                        />
                        <p className="text-xs text-[#737373] mt-1">
                          {language === 'fr' ? 'L\'email ne peut pas être modifié.' : 'Email cannot be changed.'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#e5e5e5] mb-2">
                          {language === 'fr' ? 'Bio' : 'Bio'}
                        </label>
                        <Textarea
                          value={formData.bio}
                          onChange={(e) => handleChange('bio', e.target.value)}
                          placeholder={language === 'fr' ? 'Votre bio' : 'Your bio'}
                          rows={4}
                          className="!bg-[#0a0a0a] !border-[rgba(255,255,255,0.12)] !text-[#fafafa]"
                        />
                      </div>
                      <div className="flex gap-3">
                        <Button
                          variant="gradient"
                          onClick={handleSave}
                          loading={updateLoading}
                          className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600"
                        >
                          💾 {language === 'fr' ? 'Enregistrer' : 'Save'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setIsEditing(false);
                            setFormData({
                              name: p?.fullName ?? p?.name ?? '',
                              email: p?.email ?? '',
                              bio: p?.bio ?? '',
                            });
                          }}
                          className="flex-1"
                        >
                          {language === 'fr' ? 'Annuler' : 'Cancel'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {p?.bio && (
                        <div className="p-4 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.03)]">
                          <p className="text-sm text-[#e5e5e5]">{p.bio}</p>
                        </div>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="p-4 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.03)]">
                          <p className="text-xs font-medium text-[#737373] mb-1">
                            {language === 'fr' ? 'Rôle' : 'Role'}
                          </p>
                          <p className="text-sm font-semibold text-[#e5e5e5]">
                            {profile?.role === 'super_admin'
                              ? language === 'fr'
                                ? 'Super administrateur'
                                : 'Super Administrator'
                              : language === 'fr'
                                ? 'Administrateur'
                                : 'Administrator'}
                          </p>
                        </div>
                        {(p?.lastLogin ?? profile?.last_login) && (
                          <div className="p-4 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.03)]">
                            <p className="text-xs font-medium text-[#737373] mb-1">
                              {language === 'fr' ? 'Dernière connexion' : 'Last login'}
                            </p>
                            <p className="text-sm font-semibold text-[#e5e5e5]">
                              {formatDate(p?.lastLogin ?? profile?.last_login)}
                            </p>
                          </div>
                        )}
                        {(p?.createdAt ?? profile?.created_at) && (
                          <div className="p-4 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.03)] sm:col-span-2">
                            <p className="text-xs font-medium text-[#737373] mb-1">
                              {language === 'fr' ? 'Membre depuis' : 'Member since'}
                            </p>
                            <p className="text-sm font-semibold text-[#e5e5e5]">
                              {formatDate(p?.createdAt ?? profile?.created_at)}
                            </p>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'stats' && (
              <div className="dashboard-wt-card p-6 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[var(--wt-black-2,#111)]">
                <UserStats userId={profile?.id} />
              </div>
            )}

            {activeTab === 'activity' && (
              <div className="dashboard-wt-card p-6 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[var(--wt-black-2,#111)]">
                <h3 className="dashboard-wt-card-title mb-4 text-[#e5e5e5]">
                  {language === 'fr' ? 'Activité récente' : 'Recent activity'}
                </h3>
                {activity.length === 0 ? (
                  <p className="text-[#a3a3a3] text-sm py-8 text-center">
                    {language === 'fr' ? 'Aucune activité récente.' : 'No recent activity.'}
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {activity.slice(0, 20).map((item, i) => {
                      const action = item?.action ?? item?.type ?? '—';
                      const desc = item?.description ?? item?.details ?? '';
                      const at = item?.created_at ?? item?.createdAt ?? item?.timestamp;
                      return (
                        <li
                          key={item?.id ?? i}
                          className="flex flex-wrap items-center gap-3 p-3 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.03)]"
                        >
                          <span className="text-[#22c55e] font-medium text-sm">{action}</span>
                          {desc && <span className="text-[#a3a3a3] text-sm flex-1">{desc}</span>}
                          {at && (
                            <span className="text-[#737373] text-xs">{formatDate(at)}</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>
        </section>
      </div>
    </AdminLayout>
  );
};

export default AdminProfile;
